import React from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { Building2, Check, ChevronsUpDown, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';

interface TenantSwitcherProps {
  className?: string;
}

const getRoleBadgeVariant = (role: string) => {
  switch (role) {
    case 'OWNER':
      return 'default';
    case 'ADMIN':
      return 'secondary';
    default:
      return 'outline';
  }
};

export const TenantSwitcher: React.FC<TenantSwitcherProps> = ({ className }) => {
  const { currentTenant, tenants, switchTenant, loading } = useTenant();
  const [open, setOpen] = React.useState(false);
  const [switching, setSwitching] = React.useState(false);

  const handleSwitchTenant = async (tenantId: string) => {
    if (tenantId === currentTenant?.tenantId) {
      setOpen(false);
      return;
    }

    try {
      setSwitching(true);
      await switchTenant(tenantId);
      setOpen(false);
      // Optionally reload the page to refresh all tenant-scoped data
      // window.location.reload();
    } catch (error) {
      console.error('Failed to switch tenant:', error);
    } finally {
      setSwitching(false);
    }
  };

  if (loading || !currentTenant) {
    return null;
  }

  // Only show switcher if user has multiple tenants
  if (tenants.length <= 1) {
    return (
      <div className={cn('flex items-center gap-2 px-2', className)}>
        <Building2 className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium truncate max-w-[150px]">
          {currentTenant.tenantName}
        </span>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          aria-label="Select organization"
          className={cn('justify-between w-[120px] md:w-auto', className)}
          disabled={switching}
        >
          <div className="flex items-center gap-2 truncate">
            <Building2 className="h-4 w-4 shrink-0" />
            <span className="truncate max-w-[60px] md:max-w-[120px] text-xs md:text-sm">{currentTenant.tenantName}</span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0" align="end">
        <Command>
          <CommandInput placeholder="Search organization..." />
          <CommandList>
            <CommandEmpty>No organization found.</CommandEmpty>
            <CommandGroup heading="Organizations">
              {tenants.map((tenant) => (
                <CommandItem
                  key={tenant.tenantId}
                  value={tenant.tenantId}
                  onSelect={() => handleSwitchTenant(tenant.tenantId)}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center gap-2 truncate">
                    <Building2 className="h-4 w-4 shrink-0" />
                    <span className="truncate">{tenant.tenantName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={getRoleBadgeVariant(tenant.role)} className="text-xs">
                      {tenant.role}
                    </Badge>
                    {tenant.tenantId === currentTenant.tenantId && (
                      <Check className="h-4 w-4" />
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            {/* Optional: Add ability to create new org or join another */}
            {/* 
            <CommandSeparator />
            <CommandGroup>
              <CommandItem onSelect={() => {}}>
                <Plus className="mr-2 h-4 w-4" />
                Create Organization
              </CommandItem>
            </CommandGroup>
            */}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default TenantSwitcher;
